import React, { useEffect, useMemo, useState } from 'react';
import flashcardsCsvUrl from './data/flashcards.csv';

const parseCsv = (text) => {
    const rows = [];
    let current = '';
    let currentRow = [];
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];

        if (char === '"') {
            if (inQuotes && text[i + 1] === '"') {
                current += '"';
                i += 1;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === ',' && !inQuotes) {
            currentRow.push(current.trim());
            current = '';
        } else if ((char === '\n' || char === '\r') && !inQuotes) {
            if (char === '\r' && text[i + 1] === '\n') {
                i += 1;
            }
            currentRow.push(current.trim());
            if (currentRow.some((cell) => cell)) {
                rows.push(currentRow);
            }
            currentRow = [];
            current = '';
        } else {
            current += char;
        }
    }

    if (current.length || currentRow.length) {
        currentRow.push(current.trim());
        if (currentRow.some((cell) => cell)) {
            rows.push(currentRow);
        }
    }

    return rows
        .map(([question, answer]) => ({
            question: question || '',
            answer: answer || ''
        }))
        .filter((card) => card.question && card.answer);
};

const shuffleCards = (cards) => {
    const copy = [...cards];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
};

const FlashcardGame = () => {
    const [cards, setCards] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showAnswer, setShowAnswer] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [correctCount, setCorrectCount] = useState(0);
    const [cardsPlayed, setCardsPlayed] = useState(0);

    useEffect(() => {
        const fetchCards = async () => {
            try {
                const response = await fetch(flashcardsCsvUrl);
                if (!response.ok) {
                    throw new Error('Network error');
                }
                const text = await response.text();
                const parsedCards = parseCsv(text);
                setCards(shuffleCards(parsedCards));
            } catch (err) {
                setError('Flashcards failed to load. Try refreshing the page.');
            } finally {
                setLoading(false);
            }
        };

        fetchCards();
    }, []);

    const currentCard = cards[currentIndex];

    const moveToNextCard = () => {
        if (!cards.length) {
            return;
        }
        setCurrentIndex((prev) => (prev + 1) % cards.length);
        setShowAnswer(false);
        setCardsPlayed((prev) => Math.min(prev + 1, cards.length));
    };

    const handleCardClick = () => {
        if (!cards.length) {
            return;
        }

        if (!showAnswer) {
            setShowAnswer(true);
            return;
        }

        moveToNextCard();
    };

    const markCorrect = () => {
        if (!showAnswer) {
            return;
        }
        setCorrectCount((prev) => prev + 1);
        moveToNextCard();
    };

    const shuffleDeck = () => {
        setCards((prev) => shuffleCards(prev));
        setCurrentIndex(0);
        setShowAnswer(false);
        setCorrectCount(0);
        setCardsPlayed(0);
    };

    const progressPercent = useMemo(() => {
        if (!cards.length) {
            return 0;
        }
        return Math.round((cardsPlayed / cards.length) * 100);
    }, [cardsPlayed, cards.length]);

    if (loading) {
        return (
            <section className="mena-winner__flashcards" aria-live="polite">
                <h2>Hologram Hero Flashcards</h2>
                <p>Loading your deck...</p>
            </section>
        );
    }

    if (error) {
        return (
            <section className="mena-winner__flashcards" aria-live="assertive">
                <h2>Hologram Hero Flashcards</h2>
                <p className="flashcards__error">{error}</p>
            </section>
        );
    }

    return (
        <section className="mena-winner__flashcards" aria-labelledby="flashcards-title">
            <div className="flashcards__header">
                <h2 id="flashcards-title">Hologram Hero Flashcards</h2>
                <p>Tap reveal, guess the answer, and see how many cards you can conquer in one round.</p>
            </div>

            {currentCard && (
                <>
                    <div
                        className={`flashcard ${showAnswer ? 'flashcard--flipped' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={handleCardClick}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleCardClick();
                            }
                        }}
                        aria-live="polite"
                    >
                        <div className="flashcard__inner">
                            <div className="flashcard__face flashcard__face--front">
                                <p className="flashcard__prompt">Question</p>
                                <p className="flashcard__text">{currentCard.question}</p>
                                <p className="flashcard__hint">Tap to reveal the answer</p>
                            </div>
                            <div className="flashcard__face flashcard__face--back">
                                <p className="flashcard__prompt">Answer</p>
                                <p className="flashcard__text">{currentCard.answer}</p>
                                <p className="flashcard__hint">Tap again to jump to the next card</p>
                            </div>
                        </div>
                    </div>

                    <div className="flashcard__controls">
                        <button type="button" onClick={markCorrect} disabled={!showAnswer}>
                            I Nailed It!
                        </button>
                        <button type="button" onClick={moveToNextCard}>
                            Skip / Next
                        </button>
                        <button type="button" onClick={shuffleDeck}>
                            Shuffle Deck
                        </button>
                    </div>

                    <div className="flashcard__scoreboard">
                        <div>
                            Score: <strong>{correctCount}</strong>
                        </div>
                        <div>
                            Cards played: <strong>{cardsPlayed}</strong> / {cards.length}
                        </div>
                        <div className="flashcard__progress">
                            <span style={{ width: `${progressPercent}%` }} aria-hidden="true" />
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default FlashcardGame;
